import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { concatMap, map, tap, Subscription, BehaviorSubject, catchError, mergeMap } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

import { AuthService } from '@shared/services/auth/auth.service';
import { AppService, EncryptionService, SnackbarService, LoadingService } from '@shared/services/core';

import {
  LoginCredentialWithClientSecret,
  ClientUserData,
  UserLicenseDetail,
  ProfessionalUserData,
  CookieData,
  AuthFormNotificationType,
  AuthFormType,
  UnspecifiedUserTypeData
} from '@shared/models/auth.interface';
import { BusinessError, CategorizedError } from '@modules/error/classes';
import { DashboardService } from '@shared/services/dashboard/dashboard.service';
import { LogService } from '@shared/services/core/log.service';
import { TermsAndConditionsDialogComponent } from '@core/components/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { BusinessOperationErrorTypes } from '@shared/models/error.interface';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Menu } from '@shared/models/sidenav.interface';

const demoAnimation = trigger('demoAnimation', [
  transition(':enter', [
    style({ transform: 'translateX({{ from }})' }),
    animate('200ms', style({ transform: 'translateX(0%)' }))
  ]),
])

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  animations: [demoAnimation]
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @Output() notify = new EventEmitter<AuthFormNotificationType | null>();
  @Output() formChange = new EventEmitter<AuthFormType>();
  @Input() accessCode: string = '';
  @Input() lastFormTracker = new BehaviorSubject<AuthFormType>("ACCESSCODE");

  authSubscription: Subscription | undefined;
  formValueChangesSubscription!: Subscription;
  hidePassword: boolean = true;
  toggleIcon: 'fa-eye' | 'fa-eye-slash' = 'fa-eye';

  public isPasswordVisible: boolean = false;
  loginForm: FormGroup = new FormGroup({
    userID: new FormControl('', [Validators.required, Validators.maxLength(10), clientIdFieldValidator()]),
    password: new FormControl('', Validators.required),
    rememberMe: new FormControl(false)
  })

  constructor(
    private authService: AuthService,
    private appService: AppService,
    private loadingService: LoadingService,
    private logService: LogService,
    private snackbarService: SnackbarService,
    private dashboardService: DashboardService,
    private matDialog: MatDialog,
    private router: Router,
    private encryptionService: EncryptionService,
    private deviceDetectorService: DeviceDetectorService
  ) { }

  ngOnInit(): void {
    this.formValueChangesSubscription = this.loginForm.valueChanges
    .subscribe(_ => this.notify.next(null));
  }

  login() {
    
    let newJTI: string = '';
    let newToken: string = '';
    let userID: string = '';
    let forClient = true;
    let userType: "CLT" | "PRF" | "STF" = "CLT";

    let refreshToken: string = '';
    let userLicenseDetails: UserLicenseDetail;
    let userDetails: UnspecifiedUserTypeData;

    this.notify.next(null);
    this.loadingService.showLoading();

    const requestBody: LoginCredentialWithClientSecret = {
      code: this.accessCode,
      userID: this.loginForm.get('userID')!.value,
      password: this.authService.encodeBase64(this.loginForm.get('password')?.value),
      clientId: this.appService.getAppSettings().apiCredentials.clientID,
      clientSecret: this.appService.getAppSettings().apiCredentials.clientSecret
    }

    this.authSubscription = this.authService.getTokenAndJTI(requestBody).pipe(
      tap(tokenAndJTIResponse => {
        newJTI = tokenAndJTIResponse.data.jti;
        newToken = tokenAndJTIResponse.data.token;
        this.authService.saveTokenAndJTIOnSystemStorage(tokenAndJTIResponse.data.jti, tokenAndJTIResponse.data.token);
      }),
      catchError(error => {
        console.log('Error Name:');
        console.log(error.name);
        console.log('Error Message:');
        console.log(error.message);
        console.log('Error Stack:');
        console.log(error.stack);
        if (error instanceof BusinessError)
          throw new BusinessError(error.title as BusinessOperationErrorTypes, error.description);
        else
          throw new BusinessError('LOGIN_ERROR', 'Something went wrong.');
      }),
      concatMap(tokenAndJTIResponse => this.authService.getUserIDAndRefreshToken(tokenAndJTIResponse.data)),
      tap(userIDAndRefreshTokenResponse => {
        userID = userIDAndRefreshTokenResponse.Data.UserId;
        refreshToken = userIDAndRefreshTokenResponse.Data.RefreshToken;
      }),
      concatMap(_ => this.authService.getLicenseDetails(newToken, newJTI)),
      tap(licenseDetailsResponse => {
        this.authService.checkLicenseExpiry(licenseDetailsResponse.Data);
        userLicenseDetails = licenseDetailsResponse.Data;
      }),
      concatMap(_ => this.authService.getUserTypeDetailsByID(userID, newJTI, newToken)),
      tap(userDetailsResponse => userDetails = userDetailsResponse.Data),
      concatMap(_ => this.authService.performProfessionalActivationCheck(userID, newToken, newJTI)),
      concatMap(userAllowedToLogin => {
        if (!userAllowedToLogin) throw new BusinessError('USER_NOT_LINKED_WITH_CLIENTS', 'Professional not linked with any active clients.');
 
       if (userDetails.hasOwnProperty('ClientID')) {
          userType = 'CLT';
          this.saveUserData(forClient, userDetails, userLicenseDetails, newToken, refreshToken, newJTI);
          return this.authService.updateLoginLogoutStatus(newToken, newJTI, userID, userType, "Y").pipe(
            mergeMap(_ => this.dashboardService.getMenuListData(userType, "1", newToken, newJTI))
          );
        }
        else {
          userType = 'PRF';
          forClient = false;
          this.saveUserData(forClient, userDetails, userLicenseDetails, newToken, refreshToken, newJTI);
          return this.authService.updateLoginLogoutStatus(newToken, newJTI, userID, userType, "Y").pipe(
            mergeMap(_ =>this.dashboardService.getMenuListData(userType, "0", newToken, newJTI, (userDetails as ProfessionalUserData).LevelName))
          );
        }
      }),
      map((menuOptions: Array<Menu>) => this.getLandingPageRoute(userType, menuOptions[0].optionPath, newJTI))
    ).subscribe({
      next: (landingPageRoute) => {
        // COOKIES SETTINGS.
        const rememberMeIsChecked = this.loginForm.value.rememberMe;
        const usingMobileDevice = this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
        if (rememberMeIsChecked) {
          let cookieData = {} as CookieData;
          cookieData.jti = newJTI;
          cookieData.token = newToken;
          cookieData.refreshToken = refreshToken;
          this.authService.setKeepMeLoggedIn(this.encryptionService.encryptData(JSON.stringify(cookieData)));
        }
        else {
          this.authService.removeKeepMeLoggedIN();
        }

        this.logService.appLogin("LoggedIn", { jti: newJTI, user_id: userID }, usingMobileDevice, newToken, newJTI);
        this.router.navigateByUrl(landingPageRoute as string);
      },

      error: (error) => {
        console.log('Error Name:');
        console.log(error.name);
        console.log('Error Message:');
        console.log(error.message);
        console.log('Error Stack:');
        console.log(error.stack);
        if (error instanceof BusinessError) {
          this.notify.next(error.title as AuthFormNotificationType);
        }
        else if (error instanceof CategorizedError) {
          this.snackbarService.showErrorSnackBar(error.title, error.description, 'none');
        }
        else {
          this.notify.next('LOGIN_ERROR');
        }
        this.loadingService.stopLoading();
      }
    });
  }

  togglePasswordDisplay() {
    this.hidePassword = !this.hidePassword;
    this.togglePasswordFieldIcon();
  }

  togglePasswordFieldIcon() {
    this.toggleIcon = (this.toggleIcon === 'fa-eye-slash') ? 'fa-eye' : 'fa-eye-slash';
  }

  saveUserData(forClient: boolean, userDetails: UnspecifiedUserTypeData, userLicense: UserLicenseDetail, token: string, refreshToken: string, jti: string) {
    // userIsClient = this.authService.determineIfUserIsClient(userDetails);
    if (forClient) {
      userDetails = userDetails as Exclude<UnspecifiedUserTypeData, ProfessionalUserData>;
      this.authService.saveAllUserDataOnSystemStorage<ClientUserData>(forClient, userDetails, userLicense, token, refreshToken, jti);
    } else {
      userDetails = userDetails as Exclude<UnspecifiedUserTypeData, ClientUserData>;
      this.authService.saveAllUserDataOnSystemStorage<ProfessionalUserData>(forClient, userDetails, userLicense, token, refreshToken, jti);
    }
  }

  getLandingPageRoute(userType: "CLT" | "PRF" | "STF", pageSection: string, jti: string): string {
    let userTypeSection = '';

    switch(userType) {
      case "CLT":
        userTypeSection = 'client';
        break;

      case "PRF":
        userTypeSection = 'professional';
        break;

      case "STF":
        userTypeSection = 'staff';
        break;

      default:
        userTypeSection = 'unknown';
        break;
    }

    const route = `/${userTypeSection}/${jti}/${pageSection}`;
    return route;
  }

  showPasswordRecoveryForm() {
    this.formChange.next("RECOVERY");
  }

  openTermsAndConditionsDialog(switchedOn: boolean) {
    let dialogRef: MatDialogRef<TermsAndConditionsDialogComponent, boolean>;
    if (switchedOn) {
      const dialogConfigs: MatDialogConfig = {
        id: 'privacy-policy-dialog',
        position: {
          bottom: '5vh'
        }
      };

      dialogRef = this.matDialog.open(TermsAndConditionsDialogComponent, dialogConfigs);
      dialogRef.afterClosed().subscribe(accepted => {
        if (!accepted) {
          this.loginForm.controls['rememberMe'].setValue(false);
        }
      })
    }
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.formValueChangesSubscription.unsubscribe();
  }
}

function clientIdFieldValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const clientId = control.value;

    if (clientId.length > 0 && clientId.length < 10) return { invalidLength: true }
    else return null;
  }
}